import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { Loader2 } from "lucide-react";
import { useAuthedHttp, errMsg } from "@/components/review/useAuthedHttp";
import { ReviewQueue } from "@/components/review/ReviewQueue";
import { ReviewersPanel } from "@/components/review/ReviewersPanel";
import { PeoplePanel } from "@/components/review/PeoplePanel";
import { CreditPanel } from "@/components/review/CreditPanel";

const Dashboard = () => {
  const call = useAuthedHttp();
  const { isLoaded } = useAuth();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (isLoaded) call("get", "/translations/me").then(setMe).catch((e) => setError(errMsg(e, "Could not load your account")));
  }, [isLoaded, call]);

  if (error) return <p className="mt-8 text-red-600" data-testid="review-error">{error}</p>;
  if (!me) return <Loader2 className="mt-8 animate-spin text-slate-400" />;
  if (!me.langs.length) return (
    <div className="mt-8 rounded-2xl bg-white border border-[#1C242B]/10 p-6" data-testid="review-not-approved">
      <h2 className="font-display font-bold text-lg">Waiting for approval</h2>
      <p className="mt-2 text-sm text-slate-600">You're signed in as <b>{me.emails.join(", ")}</b>. Ask the site owner to add this email as a reviewer, then refresh this page.</p>
    </div>
  );
  return (
    <div className="mt-8 grid gap-8">
      <p className="text-sm text-slate-500" data-testid="review-role-info">Signed in as <b>{me.emails[0]}</b> · {me.role === "admin" ? "Owner (all languages)" : `Reviewer: ${me.langs.join(", ").toUpperCase()}`}</p>
      <ReviewQueue call={call} langs={me.langs} />
      <CreditPanel call={call} credit={me.credit} />
      {me.role === "admin" && <ReviewersPanel call={call} allLangs={me.all_langs} />}
      {me.role === "admin" && <PeoplePanel call={call} />}
    </div>
  );
};

export default function TranslationReview() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-10" data-no-translate>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline-label">Translation review</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Volunteer suggestions</h1>
        </div>
        <Show when="signed-in"><div data-testid="review-user-button"><UserButton /></div></Show>
      </div>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </div>
  );
}
